import SettingsSection from "../../components/SettingsSection";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo } from "react";
import styles from "./Recommendations.module.scss";
import { graphql } from "../../../../__generated__/gql";
import { useMutation, useQuery } from "urql";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import Loader from "../../../../ui/Loader/Loader";
import { useToast } from "../../../../ui/Toast/ToastContext";
import Header from "../../components/Header/Header";
import Switch from "../../../../ui/Switch/Switch";
import Label from "../../../../ui/Label/Label";
import MultiTextInput from "../../../../ui/MultiTextInput/MultiTextInput";
import Tooltip from "../../../../ui/Tooltip/Tooltip";
import { Help } from "../../../../ui/Icon/Icon";
import { useDisclosure } from "../../../../ui/util/useDisclosure";
import ConfirmChangesModal from "./ConfirmChangesModal";

const KeywordRecommendationSettingsQuery = graphql(/* GraphQL */ `
  query keywordRecommendationSettings {
    keywordSettings {
      enableTopicRecommendations
      topicRecommendations
      enableCompetitorRecommendations
      competitorRecommendations
    }
  }
`);

const UpdateKeywordRecommendationSettingsMutation = graphql(/* GraphQL */ `
  mutation updateKeywordRecommendationSettings(
    $input: UpdateKeywordSettingsInput!
  ) {
    updateKeywordSettings(input: $input) {
      enableTopicRecommendations
      topicRecommendations
      enableCompetitorRecommendations
      competitorRecommendations
    }
  }
`);

const schema = z.object({
  enableTopics: z.boolean(),
  topics: z.array(z.string()),
  enableCompetitorUrls: z.boolean(),
  competitorUrls: z.array(z.string()),
});

type RecommendationFormData = z.infer<typeof schema>;

function RecommendationSettingsForm({
  defaultValues,
  onSubmit,
}: {
  defaultValues: RecommendationFormData;
  onSubmit: (data: RecommendationFormData) => Promise<void>;
}) {
  const [confirmationModalOpened, confirmationModal] = useDisclosure();

  const { showToast } = useToast();
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isSubmitting, isDirty },
  } = useForm<RecommendationFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const submit = useCallback(
    async (data: RecommendationFormData) => {
      await onSubmit(data);

      reset(data);

      showToast({
        title: "Keyword settings saved",
        variant: "success",
      });
    },
    [onSubmit, reset, showToast],
  );

  const onSubmitWrapper = useCallback(
    async (data: RecommendationFormData) => {
      const [topics, competitorUrls] = getValues(["topics", "competitorUrls"]);

      if (
        topics.every(
          (topic) =>
            !defaultValues.topics || defaultValues.topics?.includes(topic),
        ) &&
        competitorUrls.every(
          (url) =>
            !defaultValues.competitorUrls ||
            defaultValues.competitorUrls.includes(url),
        )
      ) {
        await submit(data);
      } else {
        confirmationModal.open();
      }
    },
    [
      confirmationModal,
      defaultValues.competitorUrls,
      defaultValues.topics,
      getValues,
      submit,
    ],
  );

  return (
    <form onSubmit={handleSubmit(onSubmitWrapper)}>
      <UnsavedChangesModal isDirty={isDirty} />

      <ConfirmChangesModal
        isOpen={confirmationModalOpened}
        onClose={confirmationModal.close}
        onSubmit={async () => {
          await submit(getValues());

          confirmationModal.close();
        }}
      />
      <Header
        title="Keyword Recommendations Settings"
        description="Choose how Macaw recommends keywords"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        onCancel={() => reset()}
      />
      <SettingsSection
        label="Topics"
        description="Choose which topics Macaw uses to find keyword recommendations"
      >
        <div className={styles.inputSection}>
          <Controller
            name="enableTopics"
            control={control}
            render={({ field }) => (
              <div className={styles.radioRow}>
                <label htmlFor="enableTopics" className={styles.radioLabel}>
                  <Switch
                    id={field.name}
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                  <span>Enable recommendations from topics</span>
                </label>
                <Tooltip content="Enabling recommendations from topics will include recommended keyword from your chosen topics. Topics are the same as the seed keyword phrase for keyword searches.">
                  <span>
                    <Help className={styles.helpIcon} />
                  </span>
                </Tooltip>
              </div>
            )}
          />
          <Controller
            name="topics"
            control={control}
            render={({ field }) => (
              <Label label="Topics (seed keywords)">
                <MultiTextInput {...field} />
              </Label>
            )}
          />
        </div>
      </SettingsSection>
      {/* <SettingsSection
        label="Competitors"
        description="Select competitors that Macaw analyzes to find keyword gaps"
      >
        <div className={styles.inputSection}>
          <Controller
            name="enableCompetitorUrls"
            control={control}
            render={({ field }) => (
              <div className={styles.radioRow}>
                <label
                  htmlFor="enableCompetitorUrls"
                  className={styles.radioLabel}
                >
                  <Switch
                    id={field.name}
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);
                    }}
                  />
                  <span>Enable recommendations from competitors</span>
                </label>
                <Tooltip content="Enabling recommendations from competitors will include recommended keywords that your chosen competitors currently rank for but you do not.">
                  <span>
                    <Help className={styles.helpIcon} />
                  </span>
                </Tooltip>
              </div>
            )}
          />
          <Controller
            name="competitorUrls"
            control={control}
            render={({ field }) => (
              <Label label="Competitor URLs">
                <MultiTextInput {...field} />
              </Label>
            )}
          />
        </div>
      </SettingsSection> */}
    </form>
  );
}

function Recommendations() {
  const [keywordSettingsResponse] = useQuery({
    query: KeywordRecommendationSettingsQuery,
    requestPolicy: "network-only",
  });
  const [, updateKeywordRecommendationSettings] = useMutation(
    UpdateKeywordRecommendationSettingsMutation,
  );

  const onSubmit = useCallback(
    async (data: RecommendationFormData) => {
      await updateKeywordRecommendationSettings({
        input: {
          enableTopicRecommendations: data.enableTopics,
          topicRecommendations: data.topics,
          enableCompetitorRecommendations: data.enableCompetitorUrls,
          competitorRecommendations: data.competitorUrls,
        },
      });
    },
    [updateKeywordRecommendationSettings],
  );

  const defaultValues: RecommendationFormData = useMemo(() => {
    if (!keywordSettingsResponse.data?.keywordSettings) {
      return {
        enableTopics: true,
        topics: [],
        enableCompetitorUrls: true,
        competitorUrls: [],
      };
    }

    const {
      enableTopicRecommendations,
      topicRecommendations,
      enableCompetitorRecommendations,
      competitorRecommendations,
    } = keywordSettingsResponse.data.keywordSettings;

    return {
      enableTopics: enableTopicRecommendations,
      topics: topicRecommendations ?? [],
      enableCompetitorUrls: enableCompetitorRecommendations,
      competitorUrls: competitorRecommendations ?? [],
    };
  }, [keywordSettingsResponse.data]);

  if (keywordSettingsResponse.fetching) {
    return (
      <div
        style={{
          padding: "32px 0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <RecommendationSettingsForm
      defaultValues={defaultValues}
      onSubmit={onSubmit}
    />
  );
}

export default Recommendations;
